import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompanyInfo } from '../lib/cms';

export default function QuoteSection({
  title = "Klar til at starte dit projekt?",
  subtitle = "Udfyld formularen for at modtage et skræddersyet tilbud til din opgave – helt uforpligtende.",
  compact = false,
  showDirectContacts = true,
}) {
  const companyInfo = useCompanyInfo();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    website_url: '', // Honeypot antispam
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    // Antispam honeypot check
    if (formData.website_url) {
      setIsSubmitting(false);
      navigate('/tak-for-din-henvendelse');
      return;
    }

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte til os.');
      }

      navigate('/tak-for-din-henvendelse');
    } catch (err) {
      console.error('Webform error:', err);
      setErrorMessage(err.message || 'Der opstod en fejl ved afsendelse. Ring gerne direkte til os på 40 55 55 99.');
      setIsSubmitting(false);
    }
  };

  return (
    <section className={`relative overflow-hidden ${compact ? 'py-10' : 'py-20 md:py-28 bg-[#143463] text-white'}`} id="kontakt-formular">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Heading & Text */}
          <div className={`${compact ? 'lg:col-span-12' : 'lg:col-span-5'} space-y-6`}>
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold tracking-wider uppercase bg-[#183E7F] text-[#C2CCDF] border border-[#004FA4]/40 mb-3">
                <i className="fa-sharp fa-solid fa-calculator" aria-hidden="true"></i>
                Uforpligtende tilbud
              </span>
              <h2 className={`font-light text-white tracking-tight ${compact ? 'text-2xl sm:text-3xl' : 'text-3xl sm:text-4xl lg:text-5xl'}`}>
                {title}
              </h2>
            </div>

            <p className="text-slate-300 font-light leading-relaxed text-base">
              Har du spørgsmål til dit næste projekt, eller ønsker du et tilbud? Udfyld formularen herunder, og vi vender hurtigt tilbage. Hos Hovendals står vi klar til at hjælpe dig videre – uanset om det gælder hegn, låger, porte, terrasser eller carporte.
            </p>

            <p className="text-slate-300 font-light leading-relaxed text-sm">
              Du er også altid velkommen til at kontakte os direkte via{' '}
              <a href={`tel:${companyInfo.phoneFormatted}`} className="text-[#C2CCDF] font-medium hover:text-white underline">
                {companyInfo.phone}
              </a>{' '}
              eller{' '}
              <a href={`mailto:${companyInfo.email}`} className="text-[#C2CCDF] font-medium hover:text-white underline">
                {companyInfo.email}
              </a>{' '}
              – vi bygger tillid og leverer resultater.
            </p>

            {showDirectContacts && (
              <div className="pt-6 border-t border-[#183E7F] space-y-4 font-light">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#183E7F] text-[#C2CCDF] flex items-center justify-center shrink-0">
                    <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 font-light">Ring direkte til Simon</div>
                    <a href={`tel:${companyInfo.phoneFormatted}`} className="text-base font-normal text-white hover:text-[#C2CCDF] transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#183E7F] text-[#C2CCDF] flex items-center justify-center shrink-0">
                    <i className="fa-sharp fa-solid fa-envelope" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 font-light">Send en e-mail</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-base font-normal text-white hover:text-[#C2CCDF] transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#183E7F] text-[#C2CCDF] flex items-center justify-center shrink-0">
                    <i className="fa-sharp fa-solid fa-map-pin" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 font-light">Adresse</div>
                    <div className="text-sm font-normal text-slate-200">
                      {companyInfo.address}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Form */}
          <div className={`${compact ? 'lg:col-span-12' : 'lg:col-span-7'}`}>
            <div className="bg-white p-8 sm:p-10 text-[#4B515D] border border-[#D6D3CE]/60">
              <div className="mb-6">
                <h3 className="text-2xl font-light text-[#143463]">
                  Beskriv din opgave
                </h3>
                <p className="text-xs text-[#4B515D] font-light mt-1">
                  {subtitle}
                </p>
              </div>

              {errorMessage && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-3">
                  <i className="fa-sharp fa-solid fa-triangle-exclamation text-red-600 text-sm mt-0.5" aria-hidden="true"></i>
                  <div>{errorMessage}</div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* MANDATORY Honeypot antispam field */}
                <div className="sr-only absolute left-[-9999px]" aria-hidden="true">
                  <label htmlFor="quote-website-url">Website URL (leave blank)</label>
                  <input
                    type="text"
                    id="quote-website-url"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-[#143463] uppercase tracking-wider mb-1">
                      Navn <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder="Dit fulde navn"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#D6D3CE] focus:border-[#004FA4] focus:outline-none text-[#143463] placeholder:text-slate-400 text-sm transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-xs font-medium text-[#143463] uppercase tracking-wider mb-1">
                      Telefonnummer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      placeholder="F.eks. 12 34 56 78"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#D6D3CE] focus:border-[#004FA4] focus:outline-none text-[#143463] placeholder:text-slate-400 text-sm transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-[#143463] uppercase tracking-wider mb-1">
                      E-mailadresse <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="din@email.dk"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#D6D3CE] focus:border-[#004FA4] focus:outline-none text-[#143463] placeholder:text-slate-400 text-sm transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-xs font-medium text-[#143463] uppercase tracking-wider mb-1">
                      Hvad omhandler opgaven?
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#D6D3CE] focus:border-[#004FA4] focus:outline-none text-[#143463] text-sm bg-white transition-colors"
                    >
                      <option value="">Vælg produkt / løsning</option>
                      <option value="Komposithegn">Komposithegn</option>
                      <option value="Industrihegn">Industrihegn</option>
                      <option value="Støjhegn">Støjhegn</option>
                      <option value="Panelhegn">Panelhegn</option>
                      <option value="Smedejernshegn">Smedejernshegn</option>
                      <option value="Rionethegn">Rionethegn</option>
                      <option value="Betonhegn">Betonhegn</option>
                      <option value="Træbeklædt hegn">Træbeklædt hegn</option>
                      <option value="Skydeport">Skydeport</option>
                      <option value="Dobbeltport">Dobbeltport</option>
                      <option value="Foldeport">Foldeport</option>
                      <option value="Låge">Låge / Kompositdør</option>
                      <option value="Terrasse">Terrasse / Glasafskærmning</option>
                      <option value="Carport">Carport</option>
                      <option value="Skur">Skur</option>
                      <option value="Andet">Andet / specialopgave</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-[#143463] uppercase tracking-wider mb-1">
                    Besked / projektbeskrivelse <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="4"
                    placeholder="Fortæl os kort om mål, placering og eventuelle særlige ønsker..."
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#D6D3CE] focus:border-[#004FA4] focus:outline-none text-[#143463] placeholder:text-slate-400 text-sm transition-colors"
                  ></textarea>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 px-6 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fa-sharp fa-solid fa-spinner animate-spin" aria-hidden="true"></i>
                        <span>Sender henvendelse...</span>
                      </>
                    ) : (
                      <>
                        <span>Send forespørgsel</span>
                        <i className="fa-sharp fa-solid fa-paper-plane text-xs" aria-hidden="true"></i>
                      </>
                    )}
                  </button>
                </div>

                <p className="text-[11px] text-[#4B515D] font-light text-center pt-2">
                  Vi behandler dine oplysninger fortroligt og vender hurtigst muligt tilbage med et uforpligtende svar.
                </p>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
